// eslint-disable-next-line jira/restricted/react
import React, { PureComponent } from 'react';
import find from 'lodash/find';
import Avatar from '@atlaskit/avatar';
import { Label as AtlaskitLabel } from '@atlaskit/form';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import Select from '@atlaskit/select';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import automationMessages from '@atlassian/jira-servicedesk-automation-common/src/view/common/messages';
import commonMessages from '@atlassian/jira-servicedesk-common/src/i18n';
import type { RestRunAsUserOption } from '../../../../../../rest/summary/run-as-user/get/types';
import messages from './messages';
import {
	ErrorMessageContainer,
	Intro,
	InvalidMessage,
	Li,
	OptionsLabelWrapper,
	OptionsWrapper,
} from './styled';

export type SelectedUser = {
	displayName?: string;
	accountId?: string;
	avatar?: string;
};

export type Option = {
	label: string;
	value: string;
	avatar?: string;
};

export type Props = {
	isLoading: boolean;
	isOpen: boolean;
	displayInvalidError: boolean;
	selectedUser: SelectedUser;
	runAsUserOptions: RestRunAsUserOption[];
	onFilterChange: (arg1: string) => void;
	onSelect: (arg1: SelectedUser) => void;
	onMenuOpen: () => void;
	onMenuClose: () => void;
	intl: IntlShape;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Label = (props: any) => <AtlaskitLabel {...props} />;

// eslint-disable-next-line jira/react/no-class-components
class UserPicker extends PureComponent<Props> {
	onSelect = (item: Option) => {
		// @ts-expect-error - TS2322 - Type 'RestRunAsUserOption | undefined' is not assignable to type 'RestRunAsUserOption'.
		const selectedUser: RestRunAsUserOption = find(
			this.props.runAsUserOptions,
			(runAsUserOption) => runAsUserOption.accountId === item.value,
		);

		this.props.onSelect(selectedUser);
	};

	// @ts-expect-error - TS7006 - Parameter 'user' implicitly has an 'any' type.
	formatItem(user) {
		const {
			intl: { formatMessage },
		} = this.props;
		const { avatar } = user;
		return {
			label: user.displayName || formatMessage(messages.chooseAnotherUser),
			value: user.accountId,
			avatar,
		};
	}

	// go/jfe-eslint
	// @ts-expect-error - TS7031 - Binding element 'context' implicitly has an 'any' type.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	renderOptionLabel = (option: any, { context }) => {
		const { avatar, label } = option;
		return context === 'menu' && avatar ? (
			<OptionsWrapper>
				<Avatar size="small" appearance="circle" src={avatar} />
				<OptionsLabelWrapper>{label}</OptionsLabelWrapper>
			</OptionsWrapper>
		) : (
			label
		);
	};

	renderLoadingMessage = () => this.props.intl.formatMessage(automationMessages.searching);

	renderFormattedItems() {
		const { runAsUserOptions } = this.props;
		return runAsUserOptions.map((user) => this.formatItem(user));
	}

	renderSelectedItem() {
		const { selectedUser } = this.props;
		return this.formatItem(selectedUser);
	}

	renderInvalidMessage() {
		const {
			intl: { formatMessage },
			selectedUser,
		} = this.props;
		const invalidMessage =
			selectedUser && selectedUser.displayName
				? formatMessage(messages.noPermission)
				: formatMessage(messages.userDeleted);
		return (
			<ErrorMessageContainer>
				<ErrorIcon
					primaryColor={token('color.icon.danger', colors.R300)}
					label={formatMessage(commonMessages.error)}
				/>
				<InvalidMessage>{invalidMessage}</InvalidMessage>
			</ErrorMessageContainer>
		);
	}

	render() {
		const {
			intl: { formatMessage },
			isLoading,
			displayInvalidError,
			isOpen,
			onFilterChange,
			onMenuOpen,
			onMenuClose,
		} = this.props;

		return (
			<>
				<Label htmlFor="automation-user-picker">{formatMessage(messages.defaultEventUser)}</Label>
				<Select
					inputId="automation-user-picker"
					aria-label={formatMessage(messages.defaultEventUser)}
					autoFocus
					menuIsOpen={isOpen}
					isLoading={isLoading}
					loadingMessage={this.renderLoadingMessage}
					value={this.renderSelectedItem()}
					formatOptionLabel={this.renderOptionLabel}
					options={this.renderFormattedItems()}
					onInputChange={onFilterChange}
					placeHolder={formatMessage(messages.userSelectPlaceHolder)}
					// @ts-expect-error - TS2322 - Type '(item: Option) => void' is not assignable to type '(value: { label: any; value: any; avatar: any; } | null, action: ActionMeta<{ label: any; value: any; avatar: any; }>) => void'.
					onChange={this.onSelect}
					onMenuOpen={onMenuOpen}
					onMenuClose={onMenuClose}
				/>
				{displayInvalidError && this.renderInvalidMessage()}
				<Intro>{formatMessage(messages.intro)}</Intro>
				<Intro>{formatMessage(messages.thisUserShould)}</Intro>
				<ul>
					<Li>{formatMessage(messages.explanationItem1Jsm)}</Li>
					<Li>{formatMessage(messages.explanationItem2)}</Li>
				</ul>
			</>
		);
	}
}

export default injectIntl(UserPicker);
